export default {
  employees: {
    title: 'Employees ({{count}})',
    description: 'Manage which employees have access to the wellbeing portal.',
    searchPlaceholder: 'Search',
    addEmployeeButton: 'Add employee',
    employeeAdded: '{{firstName}} {{lastName}} added',
    employeesCreated: '{{count}} employees added.',
    employeesAlreadyExisting: '{{count}} employees already exist in the system.',
    employeesAlreadyExistingNoCount: 'User already exists in the system.',
    userDeleted: 'User deleted',
    deleteUserError: 'Unable to delete user, please try again.',
    adminPermissionGranted: 'User has been granted admin rights.',
    adminPermissionRevoked: "User's admin rights have been removed.",
    managerResponsibilityRemoved: 'Managerial responsibilities removed.',
    managerResponsibilityAdded: 'Managerial responsibilities added.',
    updateUserError: 'Unable to update user.',
    deleteDialogTitle: 'Delete user',
    deleteDialogUser: 'User {{firstName}} {{lastName}} will be permanently deleted.',
    deleteDialogConfirmation: 'Are you sure you want to continue?',
    deleteDialogConfirm: 'Continue',
  },
  home: {
    title: 'Welcome to EsterCare´s Health Portal!',
    gettingStartedTitle: 'Getting started tips',
    manageEmployeesTitle: 'Manage employees',
    manageEmployeesDescription: 'Add, remove and manage employees in your organisation.',
    womensHealthPolicyTitle: "Women's health policy",
    womensHealthPolicyDescription: "Use our template to develop a women's health policy for your workplace.",
    servicesTitle: 'Your services',
    servicesSeeAll: 'View all services',
    participationTitle: 'Participation',
    participationSeeAll: 'View all statistics',
    womensHealthTrainingTitle: "Participation in women's health training",
  },
  invoices: {
    title: 'Billing',
    comingSoonTitle: 'Support for viewing your invoices is coming soon!',
    comingSoonMessage: 'In the meantime, you can email',
    comingSoonEmail: 'hello@estercare.com',
    comingSoonContinue: "and we'll help you there!",
  },
  statistics: {
    title: 'Statistics',
    description: 'Track participation and wellbeing of your employees over time.',
    summaryTitle: 'Women’s health in your organisation – in numbers',
    summaryDescription: 'A summary of how the initiative affects the health of your employees.',
    summaryHighlightIntro: 'The health programme makes health needs visible and helps employees act on them.',
    summaryHighlight:
      'The health programme makes health needs visible and helps employees act on them. Within three months, <bold>{{decreasedSymptomsPct}}%</bold> report reduced symptoms, <bold>{{lifestylePct}}%</bold> have made a lifestyle change and <bold>{{workAbilityPct}}%</bold> experience improved work ability.',
    summaryImprovedWorkAbility: 'Experience improved work ability',
    summaryLifestyleChange: 'Have made a lifestyle change',
    summaryDecreasedSymptoms: 'Have reduced symptoms within 3 months',
    summaryBasedOn: 'Based on screenings and follow-ups.',
    testimonialsTitle: 'In their own words',
    testimonialsIntro: 'This is what your employees say.',
    testimonialsFootnote: 'The quotes are anonymised free-text answers from the follow-up.',
    summaryPct: '{{pct}}%',
    notEnoughScreeningData: 'More screenings are needed to show this statistic.',
    notEnoughFollowUpData: 'More follow-ups are needed to show this statistic.',
    trainingSection: 'Training',
    womensHealthTrainingChart: "Participation in women's health training",
    moreComingSoon: 'More statistics will be displayed here in the future.',
    healthProgramTitle: 'The health programme',
    healthProgramIntro:
      'The health programme helps the employee go from insight to action through personal self-care recommendations and guidance to care when needed.',
    participantsTitle: 'Participants in the health programme',
    participantsDenominator: 'in the health programme since the start',
    followUpOutcomesTitle: 'What the health plan leads to',
    followUpOutcomesIntro:
      'Many turn the health plan into action. {{selfCarePct}}% say they follow self-care advice, and {{soughtCarePct}}% of those who need care report that they have taken the next step and sought help.',
    healthPlanRelevanceTitle: 'Found the health plan relevant',
    relevanceCompletely: 'Agree completely',
    relevancePartially: 'Agree partially',
    relevanceNotAtAll: 'Do not agree at all',
    followedSelfCareTitle: 'Follows self-care advice',
    soughtCareTitle: 'Has sought care when needed',
    outcomeImproved: 'Improved',
    outcomeUnchanged: 'Unchanged',
    outcomeWorsened: 'Worsened',
    outcomeUnit: '%',
    outcomesEyebrow: 'The effect of the service',
    outcomesIntro: 'This is how symptoms and work ability change after an employee gets their personal health plan.',
    symptomChangeTitle: 'How the symptoms change',
    symptomChangeCaption:
      'Many symptoms decrease from the baseline. Self-reported change at follow-up, compared to screening. Examples of common symptoms.',
    symptomMenstrualPain: 'Menstrual pain',
    symptomHotFlashes: 'Hot flashes',
    symptomBrainFog: 'Brain fog',
    symptomSleepDifficulties: 'Sleep difficulties',
    symptomIrritability: 'Irritability',
    productivityChangeTitle: 'How productivity changes',
    productivityChangeCaption:
      'Self-reported change in productivity at follow-up, among those who initially had moderate to high productivity impact.',
    productivityChangeCaptionWithData:
      '<bold>{{improvedPct}}% experience improved productivity</bold> among those whose issues initially had a moderate to high impact on work. A further {{unchangedPct}}% experience unchanged productivity. (Self-reported at screening and follow-ups.)',
    competenceTitle: 'Training',
    competenceIntro:
      'Knowledge among both employees and managers is an important part of creating a more sustainable work environment around women’s health. Here you can see how many have started and completed the training courses.',
    completionCompleted: 'Completed',
    completionInProgress: 'In progress',
    completionNotStarted: 'Not started',
    completionUnit: 'people',
    eligibleCoworkers: 'All employees, {{count}} in total',
    eligibleManagers: 'Employees with people management responsibility, {{count}} in total',
    womensHealthTrainingName: "Introductory training in women's health",
    managerTrainingName: 'Manager training',
    needsTitle: 'The needs in your organisation',
    needsIntro:
      'The screening gives a picture of which issues related to hormonal and gynaecological health exist among the employees who took part, how severe they are and how they affect everyday life and work.',
    needsIntroDetail:
      '<bold>{{severePct}}% of screened employees have at least one severe issue and {{carePct}}% are recommended to seek care.</bold> This shows that the programme reaches people with concrete health needs and can help them on to the right support.',
    severityByConditionTitle: 'Most common symptoms',
    severityByConditionCaptionEmpty: 'Number of women per symptom. A woman can have more than one symptom.',
    severityByConditionCaption: 'Number of women with symptoms. A woman can have more than one symptom.',
    severityByConditionUnit: 'women',
    conditionPmsPmds: 'PMS & PMDD',
    conditionMenopause: 'Perimenopause & menopause',
    conditionMenstrualPain: 'Menstrual pain',
    conditionIncontinence: 'Incontinence',
    conditionHeavyBleeding: 'Heavy bleeding',
    impactLow: '0–3 low',
    impactModerate: '4–6 moderate',
    impactHigh: '7–10 high',
    workImpactProductivityTitle: 'Impact on productivity at work',
    workImpactProductivityCaption: 'How the women rate the impact on productivity, by severity',
    workImpactTierMild: 'Mild symptoms',
    workImpactTierModerate: 'Moderate symptoms',
    workImpactTierSevere: 'Severe symptoms',
    workImpactTierMildModerate: 'Mild and moderate symptoms',
    workImpactTierAll: 'All symptoms',
    percentValue: '{{value}}%',
    ageDistributionTitle: 'Age distribution',
    ageDistributionCaption: 'Age at the time of screening.',
    careLevelTitle: 'Recommended to seek care',
    severityTitle: 'Have at least one severe symptom',
    lifePhasesTitle: 'Support that follows women <highlight>through life</highlight>.',
    lifePhasesDescription:
      "From periods and PMS, through pregnancy and the time after birth, to menopause. EsterCare meets women's needs with targeted interventions and programmes in every phase.",
    lifePhasesExample:
      'Example: Anna gets access to the platform at age 26 and meets the right support in every phase.',
    lifePhasesAge1: 'Age 26',
    lifePhasesLabel1: 'Period & PMS',
    lifePhasesAge2: 'Age 31',
    lifePhasesLabel2: 'Fertility & pregnancy',
    lifePhasesAge3: 'Age 33',
    lifePhasesLabel3: 'After pregnancy',
    lifePhasesAge4: 'Age 45',
    lifePhasesLabel4: 'Perimenopause',
    lifePhasesAge5: 'Age 50',
    lifePhasesLabel5: 'Menopause',
    lifePhasesConditionsLabel: 'Regardless of life phase, we also detect diseases/conditions such as:',
    lifePhasesCondition1: 'Endometriosis',
    lifePhasesCondition2: 'PMOS',
    lifePhasesCondition3: 'Thyroid problems',
    lifePhasesCondition4: 'Yeast infection',
    lifePhasesCondition5: 'Vulvodynia',
    exportReport: 'Export report',
    exportWarningTitle: 'Before you export',
    exportUnsupportedText:
      'Exporting the report is supported in Chrome and Edge. In other browsers the layout may be incorrect.',
    exportInsufficientDataText:
      'You do not yet have enough data for every part of the report. Charts without enough data show an explanation instead of figures.',
    exportAnyway: 'Export anyway',
    reportGeneratedAt: 'Exported {{date}}',
    reportFooter:
      'This report is produced by EsterCare Workplace Solutions. All statistics are aggregated, anonymised and only shown when the underlying data is large enough.',
  },
  services: {
    activeTitle: 'Activated Services',
    title: 'Services',
    description1: 'Here we have gathered all services included in our health portal.',
    description2: 'As an administrator, you can easily activate the services you wish to include in your offering.',
    addMoreTitle: 'Add More Services',
  },
  policy: {
    title: "Women's Health Policy",
    description:
      'Here we offer a template that can be used as a starting point for developing a policy for your workplace.',
    downloadLabel: 'Download',
    backgroundTitle: 'Background & Purpose',
    actionsTitle: 'Actions',
    principlesTitle: 'Fundamental Principles',
    responsibilityTitle: 'Responsibility',
    educationSubtitle: 'Training & Awareness',
    preventiveSubtitle: 'Preventive & Health-Promoting Measures',
    workEnvironmentSubtitle: 'Work Environment',
    backgroundParagraph1:
      "Women face different health challenges during various phases of their lives, from reproductive health to menopause. Symptoms and conditions related to their gynaecological and hormonal health exist without male equivalents. Women's health must be taken seriously and addressed in the workplace to ensure that women are given the same opportunities to perform as men.",
    backgroundParagraph2:
      "Research shows that women's health is often overlooked or marginalised both within the healthcare system and in workplaces. It is therefore important that employers actively take responsibility for addressing these needs and creating an environment where women feel heard and supported in their health concerns.",
    backgroundParagraph3:
      "By acknowledging that gender-specific health issues exist, our company takes a step towards promoting a more equal and healthy workplace. Implementing a women's health policy demonstrates our commitment.",
    backgroundParagraph4:
      "With the implementation of this policy, we will ensure that our employees have access to relevant information, resources and support related to women's health.",
    backgroundParagraph5:
      "By actively supporting women's health, we are not only investing in our employees' wellbeing, but also in our organisation's long-term success and sustainability.",
    principlesParagraph1:
      "The women's health policy aims to promote equal opportunities for all employees, regardless of gender, to perform and experience wellbeing in the workplace. The policy is designed to create an inclusive work environment where each individual is treated with respect and given the same opportunities to develop and contribute to the company's success.",
    principlesParagraph2:
      'All employees have the right to a healthy and safe working environment. We recognise that women may have specific health needs and challenges that affect their wellbeing and performance in the workplace.',
    principlesParagraph3:
      'We commit to respecting and protecting the privacy and dignity of all employees. No one shall be discriminated against or treated unfairly based on gender, health status or other personal circumstances.',
    principlesParagraph4:
      'We promote open and transparent communication regarding health matters. All employees should feel comfortable discussing health-related concerns and receiving support when needed.',
    principlesParagraph5:
      'We strive to offer flexibility and accommodations to meet individual health needs and promote a healthy work-life balance.',
    principlesParagraph6:
      "We commit to continuously educating and informing all employees about women's health and gender equality to create a more aware and supportive work environment.",
    responsibilityParagraph1:
      "It is important to clearly define and allocate responsibility for the implementation and compliance with the women's health policy in our workplace. Below is a description of those responsible for different aspects of the policy:",
    responsibilityManagement: 'Executive Management:',
    responsibilityManagementText:
      'Responsible for establishing the overall objectives and guidelines of the policy and for providing the necessary resources for its implementation.',
    responsibilityHR: 'HR Department:',
    responsibilityHRText:
      'Has primary responsibility for administering and monitoring the implementation of the policy. This includes ensuring the policy is communicated to all employees, conducting training and evaluations, and handling any complaints or violations of the policy.',
    responsibilityManagers: 'Managers and Leaders:',
    responsibilityManagersText:
      "Expected to support and promote the policy among their team members by acting as role models and advocates for gender equality and women's health. They are also responsible for encouraging open communication and reporting of any issues related to the policy.",
    responsibilityEmployees: 'All Employees:',
    responsibilityEmployeesText:
      "Expected to follow the policy and actively contribute to creating an inclusive and healthy work environment by treating each other with respect and acting in accordance with the policy's principles and guidelines.",
    principlesParagraph7:
      'This policy applies to all employees within the company, regardless of their role, geographical location or employment status.',
    actionsIntro:
      'Actions include initiatives and resources aimed at preventing health issues or reducing the risk of their occurrence among our female employees. This includes offering training to increase knowledge and break taboos, as well as providing health-promoting support when needed.',
    educationPoint1:
      "We offer all employees the opportunity to access digital training on women's health to ensure everyone gains fundamental knowledge about the female body and various challenges related to it.",
    educationPoint2:
      'We also ensure access to scientific information about the female body through articles and podcast episodes.',
    educationPoint3:
      "We offer all managers digital training on women's health, focusing on how various issues can affect individuals and their ability to perform work tasks adequately.",
    educationPoint4:
      "We actively work to reduce stigma and taboos related to menstruation, menopause and other women's health topics through open communication and educational initiatives.",
    educationPoint5:
      'Each manager is responsible for disseminating information about and implementing training to ensure we continuously improve and promote this work.',
    preventivePoint1:
      "We offer all female employees an annual women's health screening programme to identify and address symptoms that can be treated, which if left untreated, cause unnecessary suffering and reduced quality of life.",
    preventivePoint2:
      'We offer all female employees the opportunity to receive a personalised health plan tailored to their unique circumstances, challenges and needs.',
    preventivePoint3: 'We offer all female employees health consultations with a midwife.',
    workEnvironmentPoint1:
      'We provide free menstrual products in all workplace toilets or to take away if work is performed outside the office.',
    workEnvironmentPoint2:
      'We ensure that our female employees have access to toilets with running water during the working day.',
    workEnvironmentPoint3:
      "We offer flexible working arrangements, the option to work from home, and short breaks to manage symptoms related to women's health. Additionally, health consultations, medical appointments or visits within the framework of national screening programmes related to women's health may be conducted during working hours.",
    principleRespect: 'Respect and Dignity:',
    principleRespectText:
      'We strive to treat all employees with respect and dignity regardless of age or gender. We promote an inclusive work environment where equality is celebrated, health conditions are not diminished or ridiculed, and where everyone has the right to equal opportunities.',
    principleNonDiscrimination: 'Prevention of Discrimination and Ridicule:',
    principleNonDiscriminationText:
      'We expect all employees to show respect and tolerance towards one another and to act decisively if they observe discriminatory or demeaning behaviour. We take such situations seriously and investigate all allegations to ensure appropriate measures are taken.',
    principleInclusive: 'Inclusive workplace:',
    principleInclusiveText:
      'We strive to be an inclusive workplace where all employees are treated equally and given the same opportunities. We actively work to promote a culture of openness and understanding where all employees feel comfortable discussing matters that affect their work performance.',
  },
  service: {
    priceProposalMobile: 'Get a price quote',
    priceProposalDesktop: 'Get a price quote and activate the service for your employees',
    activeStatus: 'ACTIVE',
    loadingMessage: 'Please wait while we process your request.',
    successTitle: 'We have received your request!',
    successDescription:
      "We will respond with a price quote within 1-2 working days. Afterwards, we'll help you quickly and smoothly implement the service at your organisation.",
    errorTitle: 'Something went wrong',
    errorDescription: 'Please try again or contact our support team.',
  },
  certification: {
    pageTitle: 'Certification',
    title: 'Equal Health Certification',
    description:
      'Equal Health Certification is a quality mark for workplaces that want to work in a structured way with women’s health and equal health.\nThe certification helps you understand where your organisation stands today, highlights what you already do well, and identifies areas where you can develop.\n\nThe next step is to fill in the certification survey. You answer questions on behalf of your organisation, and based on the answers a report is compiled with your strengths and areas for improvement. You will also receive concrete recommendations that help you prioritise next steps and create long-term change.',
    consentText:
      'By continuing you consent to EsterCare processing and storing the information you provide in the certification survey.',
    startButton: 'Start the certification',
    continueButton: 'Continue with the certification',
    uploadTitle: 'Attach your women’s health policy',
    uploadDescription:
      'Attach your policy as a PDF and our team will take it into account in the assessment. You can skip this step if you have no policy to attach.',
    uploadFileTooLarge: 'The file is too large. Each file may be at most {{maxSize}} MB.',
    uploadTooManyFiles: 'You can attach at most {{maxFiles}} files.',
    uploadWrongFileType: 'Only PDF files can be attached.',
    uploadRejected: 'The file could not be attached. Please try another file.',
    removeDocument: 'Remove {{fileName}}',
    submitTitle: 'Time to submit',
    submitDescription:
      'All questions have now been answered. Once the assessment is complete, you can view your report, including strengths, areas for improvement and recommended next steps.',
    submitNextSteps: 'We will be in touch by email once it is available in the portal.',
    submitButton: 'Submit answers',
    previousQuestionError: 'We could not fetch the previous question. Please try again.',
    submittedTitle: 'Thank you!',
    submittedDescription: 'We will be in touch as soon as your results are ready.',
    pendingTitle: 'Your answers are being processed',
    pendingDescription: 'You will receive an email when the report is ready',
    resultsTitle: 'Equal Health Certification',
    resultsDescription:
      'A quality mark for equal health in the workplace - based on global standards. It guides employers in creating safer, healthier and more inclusive workplaces for women.',
    notAchievedCertification: 'Your organisation has not yet achieved the Equal Health Certification',
    achievedCertification: 'Your organisation has achieved Equal Health {{level}}',
    downloadLogo: 'Download logo',
    levelBadge: 'LEVEL {{level}}',
    foundationTitle: 'Equal Health Foundation',
    foundationDescription:
      'Your organisation has taken important first steps toward integrating women’s health into workplace policies and practices. This level recognises awareness, early initiatives and a clear ambition to create a more inclusive and supportive workplace for all employees.',
    advancedTitle: 'Equal Health Advanced',
    advancedDescription:
      'Your organisation has introduced structured routines and an active approach to women’s health that runs through several parts of the business. This level shows a clear commitment and continuous work toward a more equal and health-promoting workplace.',
    excellenceTitle: 'Equal Health Excellence',
    excellenceDescription:
      'Your organisation is a leader in workplace women’s health and has made it a natural part of its culture and strategy. This level represents the highest level of commitment to a safe, inclusive and health-promoting workplace for all employees.',
    reportTitle: 'Report',
    reportDescription:
      'Here you will find your certification report, which shows the result of EsterCare’s review of your organisation’s work on women’s health in the workplace. The report presents your certification level, a summary of your strengths and concrete areas for improvement within the four assessed areas: policy and governance, work environment and adjustments, competence and culture, and health initiatives and flexibility.',
    reportDownloadCta:
      'Download the report to take part of the full assessment and the recommendations for next steps.',
    downloadReport: 'Download the report',
    nextStepsTitle: 'Recommended next steps',
  },
  support: {
    questionsTitle: 'Do you have any questions or concerns?',
    contactMessage: 'Email us at',
    contactEmail: 'hello@estercare.com',
    contactContinue: "and we'll be happy to help!",
  },
} as const
